import { Injectable, effect, computed } from '@angular/core';
import { lastValueFrom, Subscription, timer, BehaviorSubject } from 'rxjs';
import { EventsService } from 'api/services';
import { SortType, EventType, UserRoles } from 'api/models';
import {
    setEventsLoading,
    setEventsError,
    setEvents,
    addEvent,
    bulkAddEvents,
    EventWithOrgSiteKey,
    getEvents,
    getEventsCount,
} from '@app/stores/eventsStore';
import { searchState } from '@app/stores/searchStore';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root',
})
export class EventsPaginatedService {
    private allowedRoles = [
        UserRoles.Carrier,
        UserRoles.Consignee,
        UserRoles.Consignor,
        UserRoles.OrganizationAdmin,
        UserRoles.SiteAdmin,
        UserRoles.SelfCheckin,
    ];

    private initialLoadDone = false;
    private lastGt: number = 0;
    private pollingSubscription?: Subscription;
    private isPollingActive = false;

    // Notification bell functionality
    private newNotificationsSubject: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);
    public newNotifications$ = this.newNotificationsSubject.asObservable();
    private lastKnownEventCount = 0;

    // localStorage key for polling timestamp
    private readonly STORAGE_KEY_LAST_POLL = 'events_last_poll_timestamp';

    // Polling configuration
    private readonly POLL_INTERVAL_MS = 15000; // 15 seconds

    constructor(
        private eventsService: EventsService,
        private sessionService: SessionService,
        private storageService: StorageService,
        private translateService: TranslateService
    ) {
        // Watch for changes in eventsStore to trigger notification bell
        effect(() => {
            const currentCount = getEventsCount();

            // Only show notification if count increased (new events added)
            if (
                currentCount > this.lastKnownEventCount &&
                this.lastKnownEventCount > 0
            ) {
                const receiveNotifications = this.getNotificationSetting();
                if (receiveNotifications) {
                    this.newNotificationsSubject.next(true);
                }
            }

            this.lastKnownEventCount = currentCount;
        });

        // Setup visibility change handler to check for new events when tab becomes visible
        this.setupVisibilityChangeHandler();
    }

    // Computed signal for filtered events with translation support
    public filteredEvents = computed(() => {
        const events = getEvents();
        const currentSearchState = searchState();
        const searchText = currentSearchState.searchText?.toLowerCase().trim();
        const filterKey = currentSearchState.selectedFilterKey?.keyName;

        // If no search text, return all events
        if (!searchText) {
            return events;
        }

        // Filter events based on search text and filter key
        return events.filter((item: EventWithOrgSiteKey) => {
            // If a specific filter key is selected, search only in that field
            if (filterKey) {
                if (filterKey === 'eventType') {
                    // Search in both raw eventType and translated value
                    const rawEventType =
                        item.payload?.eventType?.toLowerCase() || '';
                    const translationKey =
                        'OUTGOING-GOODS.NOTIFICATION-VIEW.TABLE-HEADER.TYPES.' +
                        item.payload?.eventType;
                    const translatedValue = this.translateService
                        .instant(translationKey)
                        .toLowerCase();

                    return (
                        rawEventType.includes(searchText) ||
                        translatedValue.includes(searchText)
                    );
                } else if (filterKey === 'bundleNr') {
                    // Search in deliveryNoteBundles
                    if (item.payload && 'deliveryNoteBundles' in item.payload) {
                        const payload = item.payload as any;
                        return payload.deliveryNoteBundles?.some(
                            (bundle: any) =>
                                bundle.bundleNumber
                                    ?.toLowerCase()
                                    .includes(searchText)
                        );
                    }
                    return false;
                } else if (filterKey === 'organizationSiteKey') {
                    return item.organizationSiteKey
                        ?.toLowerCase()
                        .includes(searchText);
                }
                // Unknown filter key - return no results (defensive)
                return false;
            }

            // Full-text search across all relevant fields (only when no filterKey selected)
            const rawEventType = item.payload?.eventType?.toLowerCase() || '';
            const translationKey =
                'OUTGOING-GOODS.NOTIFICATION-VIEW.TABLE-HEADER.TYPES.' +
                item.payload?.eventType;
            const translatedEventType = this.translateService
                .instant(translationKey)
                .toLowerCase();
            const orgSiteKey = item.organizationSiteKey?.toLowerCase() || '';

            // Search in bundle numbers
            let bundleNumbers = '';
            if (item.payload && 'deliveryNoteBundles' in item.payload) {
                const payload = item.payload as any;
                if (payload.deliveryNoteBundles) {
                    bundleNumbers = payload.deliveryNoteBundles
                        .map((bundle: any) => bundle.bundleNumber || '')
                        .join(' ')
                        .toLowerCase();
                }
            }

            // Search in delivery note numbers
            let deliveryNoteNumbers = '';
            if (item.payload && 'deliveryNoteBundles' in item.payload) {
                const payload = item.payload as any;
                if (payload.deliveryNoteBundles) {
                    payload.deliveryNoteBundles.forEach((bundle: any) => {
                        if (bundle.deliveryNotes) {
                            deliveryNoteNumbers += bundle.deliveryNotes
                                .map(
                                    (note: any) => note.deliveryNoteNumber || ''
                                )
                                .join(' ')
                                .toLowerCase();
                        }
                    });
                }
            }

            return (
                rawEventType.includes(searchText) ||
                translatedEventType.includes(searchText) ||
                orgSiteKey.includes(searchText) ||
                bundleNumbers.includes(searchText) ||
                deliveryNoteNumbers.includes(searchText)
            );
        });
    });

    async loadEventsPaginated(
        organizationSiteKey?: string,
        isBackgroundPoll: boolean = false
    ): Promise<void> {
        // Only show loading state for user-initiated loads, not background polling
        if (!isBackgroundPoll) {
            setEventsLoading(true);
            setEventsError(null);
        }

        try {
            const session = this.sessionService.getSession();
            if (
                !session ||
                !session.organizations ||
                session.organizations.length === 0
            ) {
                console.log(
                    'EventsPaginatedService: No session or organizations available'
                );
                setEventsError('GENERAL.ERRORS.NO_SESSION_AVAILABLE');
                return;
            }

            const organizationKey = session.organizations[0]._key;

            // Filter sites same way as notifications service
            let siteKeys: string[];
            if (organizationSiteKey) {
                // If specific site key provided, use it (but still filter for selfCheckin)
                const allSites = this.sessionService.getMyOrganizationSites();
                const targetSite = allSites.find(
                    site => site._key === organizationSiteKey
                );
                if (
                    targetSite &&
                    targetSite.selfCheckin &&
                    targetSite.roles!.some((role: UserRoles) =>
                        this.allowedRoles.includes(role)
                    )
                ) {
                    siteKeys = [organizationSiteKey];
                } else {
                    console.log(
                        'EventsPaginatedService: Target site not found or no selfCheckin enabled'
                    );
                    setEvents([]);
                    return;
                }
            } else {
                // Filter for sites with selfCheckin enabled and allowed roles (same as notifications service)
                siteKeys = this.sessionService
                    .getMyOrganizationSites()
                    .filter(allSite =>
                        allSite.roles!.some((role: UserRoles) =>
                            this.allowedRoles.includes(role)
                        )
                    )
                    .filter(allSite => allSite.selfCheckin)
                    .map(allSite => allSite._key);
            }

            // Don't make API call if no organization sites are selected
            if (!siteKeys || siteKeys.length === 0) {
                console.log(
                    'EventsPaginatedService: No valid site keys available (no selfCheckin enabled)'
                );
                setEvents([]);
                return;
            }

            // Time range logic
            let gt: number;
            if (!this.initialLoadDone) {
                // Initial load: get events from last 8 hours
                gt = new Date().getTime() - 8 * 60 * 60 * 1000;
                this.lastGt = gt;
            } else {
                // Subsequent loads: get events from last update
                gt = this.lastGt;
            }

            const eventTypes: Array<EventType> = [
                EventType.SelfCheckinCheckinFinished,
                EventType.SelfCheckinRegistrationCompleted,
                EventType.SelfCheckinDriverWaiting,
            ];

            const params = {
                organizationKey,
                sort: 'desc' as SortType,
                gt,
                siteKeys,
                eventTypes,
                limit: 50,
            };

            console.log(
                'EventsPaginatedService: Loading events with params:',
                params
            );

            const response = await lastValueFrom(
                this.eventsService.getEvents(params)
            );

            console.log('EventsPaginatedService: API response:', response);

            if (response && Array.isArray(response)) {
                const currentEvents = getEvents();

                // Filter out events that already exist (same logic as notifications service)
                const newEvents = response.filter(
                    event =>
                        !currentEvents.some(
                            currentEvent =>
                                currentEvent.eventKey === event.eventKey
                        )
                );

                if (newEvents.length > 0 || !this.initialLoadDone) {
                    // Map the response to include orgSiteKey for consistency
                    const eventsWithOrgSiteKey: EventWithOrgSiteKey[] =
                        newEvents.map(event => ({
                            ...event,
                            read_only: false,
                            orgSiteKey: event.organizationSiteKey,
                        }));

                    if (!this.initialLoadDone) {
                        // Initial load: replace all events
                        const allEventsWithOrgSiteKey: EventWithOrgSiteKey[] =
                            response.map(event => ({
                                ...event,
                                read_only: false,
                                orgSiteKey: event.organizationSiteKey,
                            }));
                        setEvents(allEventsWithOrgSiteKey);
                        this.initialLoadDone = true;
                    } else {
                        // Subsequent loads: add new events
                        bulkAddEvents(eventsWithOrgSiteKey);
                    }

                    // Update gt timestamp to latest event (same logic as notifications service)
                    const allEvents = [
                        ...currentEvents,
                        ...eventsWithOrgSiteKey,
                    ];
                    if (allEvents.length > 0) {
                        this.lastGt = Math.max(
                            ...allEvents.map(event =>
                                new Date(event.dateCreated).getTime()
                            ),
                            this.lastGt
                        );
                    }

                    console.log(
                        'EventsPaginatedService: Successfully loaded',
                        newEvents.length,
                        'new events, total:',
                        getEvents().length
                    );
                } else {
                    // No new events, update gt to recent time for next poll
                    this.lastGt = new Date().getTime() - 60 * 1000; // 1 minute ago
                    console.log('EventsPaginatedService: No new events found');
                }

                if (!this.initialLoadDone) {
                    this.initialLoadDone = true;
                }
            } else {
                console.log('EventsPaginatedService: No events in response');
                if (!this.initialLoadDone) {
                    setEvents([]);
                    this.initialLoadDone = true;
                }
            }
        } catch (error: any) {
            console.error(
                'EventsPaginatedService: Error loading events:',
                error
            );

            // Only show error for user-initiated loads, not background polls
            if (!isBackgroundPoll) {
                // Extract request ID from error if available
                const requestId =
                    error?.headers?.get?.('x-request-id') ||
                    error?.error?.requestId ||
                    null;
                const errorMessage = requestId
                    ? `GENERAL.ERRORS.FAILED_TO_LOAD_EVENTS|${requestId}`
                    : 'GENERAL.ERRORS.FAILED_TO_LOAD_EVENTS';

                setEventsError(errorMessage);

                // Reset pagination to empty state on error
                const { resetPaginationToEmpty } = await import(
                    '@app/stores/searchStore'
                );
                resetPaginationToEmpty();
            }
        } finally {
            // Only update loading state for user-initiated loads
            if (!isBackgroundPoll) {
                setEventsLoading(false);
            }
        }
    }

    async loadEventsForSite(organizationSiteKey: string): Promise<void> {
        return this.loadEventsPaginated(organizationSiteKey);
    }

    async refreshEvents(): Promise<void> {
        // Reset state for fresh load
        this.initialLoadDone = false;
        this.lastGt = 0;
        return this.loadEventsPaginated();
    }

    /**
     * Reset the initial load state to force a fresh load next time
     */
    public resetInitialLoad(): void {
        this.initialLoadDone = false;
        this.lastGt = 0;
    }

    /**
     * Starts polling for events every 15 seconds.
     * Note: Browser will throttle this when tab is in background (by design).
     * We use Page Visibility API to catch up immediately when tab becomes visible.
     */
    public startPolling(): void {
        if (this.isPollingActive) {
            console.log('EventsPaginatedService: Polling already active');
            return;
        }

        console.log('EventsPaginatedService: Starting polling');
        this.isPollingActive = true;

        // Do initial load immediately (shows loading state)
        this.loadEventsPaginated()
            .then(() => {
                this.saveLastPollTimestamp();
            })
            .catch(error => {
                console.error('EventsPaginatedService: Initial load error:', error);
            });

        // Poll every 15 seconds in the background (silent, no loading state)
        // Note: This will be throttled by browser when tab is hidden - this is expected
        this.pollingSubscription = timer(15000, 15000).subscribe(async () => {
            if (!this.isPollingActive) {
                return;
            }

            // Check if we should continue polling (has selfCheckin sites)
            const hasSelfCheckinSites = this.sessionService
                .getMyOrganizationSites()
                .filter(site =>
                    site.roles!.some((role: UserRoles) =>
                        this.allowedRoles.includes(role)
                    )
                )
                .some(site => site.selfCheckin);

            if (hasSelfCheckinSites) {
                // Pass true for isBackgroundPoll to prevent loading state flash
                await this.loadEventsPaginated(undefined, true)
                    .then(() => {
                        this.saveLastPollTimestamp();
                    })
                    .catch(error => {
                        console.error(
                            'EventsPaginatedService: Polling error:',
                            error
                        );
                    });
            }
        });
    }

    /**
     * Stops polling for events
     */
    public stopPolling(): void {
        console.log('EventsPaginatedService: Stopping polling');
        this.isPollingActive = false;

        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = undefined;
        }
    }

    /**
     * Sets the notification setting to either enable or disable notifications.
     * @param setNotification A boolean value indicating whether to enable or disable notifications.
     */
    public setNotificationSetting(setNotification: boolean): void {
        const userKey = this.sessionService.getSession()._key;
        this.storageService.setItem(
            'receiveNotifications' + '_' + userKey,
            setNotification
        );
    }

    /**
     * Retrieves the current notification setting.
     * @returns A boolean value indicating whether notifications are enabled or disabled.
     */
    public getNotificationSetting(): boolean {
        const userKey = this.sessionService.getSession()._key;
        const receiveNotifications = this.storageService.getItem(
            'receiveNotifications' + '_' + userKey
        );

        if (
            receiveNotifications === 'true' ||
            receiveNotifications === 'false'
        ) {
            const isNotificationEnabled = JSON.parse(receiveNotifications);
            return isNotificationEnabled;
        } else {
            return true;
        }
    }

    /**
     * Sets the new notifications indicator to false.
     */
    public setNewNotificationsToFalse(): void {
        this.newNotificationsSubject.next(false);
    }

    // ========== localStorage Timestamp Polling Methods ==========

    /**
     * Saves the current timestamp to localStorage as the last poll time.
     * Uses user-specific key to prevent conflicts in shared browsers.
     */
    private saveLastPollTimestamp(): void {
        try {
            const userKey = this.sessionService.getSession()._key;
            const key = `${this.STORAGE_KEY_LAST_POLL}_${userKey}`;
            this.storageService.setItem(key, Date.now().toString());
        } catch (error) {
            console.warn('Failed to save last poll timestamp:', error);
        }
    }

    /**
     * Sets up visibility change handler to aggressively catch up when tab becomes visible.
     * When user returns to tab, we immediately poll if enough time has passed since last poll.
     * This compensates for browser throttling that occurs in background tabs.
     */
    private setupVisibilityChangeHandler(): void {
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden && this.isPollingActive) {
                // Tab became visible - check if we should force an immediate catch-up poll
                try {
                    const userKey = this.sessionService.getSession()._key;
                    const key = `${this.STORAGE_KEY_LAST_POLL}_${userKey}`;
                    const lastPollStr = this.storageService.getItem(key);

                    // Calculate time since last successful poll
                    const now = Date.now();
                    const lastPoll = lastPollStr
                        ? parseInt(lastPollStr, 10)
                        : 0;
                    const elapsed = now - lastPoll;

                    // Only force immediate poll if POLL_INTERVAL_MS has elapsed
                    // This prevents redundant poll if user quickly switches tabs
                    if (
                        !lastPollStr ||
                        isNaN(lastPoll) ||
                        elapsed >= this.POLL_INTERVAL_MS
                    ) {
                        console.log(
                            `EventsPaginatedService: Tab visible after ${Math.round(elapsed / 1000)}s, catching up now`
                        );

                        this.loadEventsPaginated(undefined, true)
                            .then(() => {
                                this.saveLastPollTimestamp();
                            })
                            .catch(error => {
                                console.error(
                                    'EventsPaginatedService: Visibility catch-up error:',
                                    error
                                );
                            });
                    } else {
                        console.log(
                            `EventsPaginatedService: Tab visible but poll happened ${Math.round(elapsed / 1000)}s ago, skipping redundant poll`
                        );
                    }
                } catch (error) {
                    console.warn(
                        'Error in visibility change handler:',
                        error
                    );
                    // On error, poll anyway to be safe
                    this.loadEventsPaginated(undefined, true)
                        .then(() => {
                            this.saveLastPollTimestamp();
                        })
                        .catch(error => {
                            console.error(
                                'EventsPaginatedService: Visibility catch-up error:',
                                error
                            );
                        });
                }
            }
        });
    }
}
